import {find, remove, filter, pick, sortBy} from 'lodash';
import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import store from '@/stores';

export interface IBudget {
	id: string;
	locationBase: string;
	programId: string;
	roas: number;
	estimatedDailySales: number;
	dailyBudget: number;
	order: number;
}

export interface IIncentiveCategory {
	base: string;
	locationBase: string;
	campaignBase: string;
	title: string;
}

export interface IIncentive {
	base: string;
	locationBase: string;
	campaignBase: string;
	incentiveCategoryBase: string;
	title: string;
	subtitle: string;
}

export interface ILocation {
	base: string | null;
	title: string;
	address: string;
	lat: number;
	lng: number;
}

export interface IBillingSale {
	locationBase: string | null;
	interval: TInterval;
	timestamp: number;
	gross: number;
	delivered: number;
	roas: number;
	total: number;
}

export interface ISales {
	locationBase: string | null; // Null is used for totals/All Locations
	interval: TInterval;
	timestamp: number;
	redemptions: number;
	gross: number;
	net: number;
}

export interface ITotalSales {
	locationBase: string | null; // Null is used for totals/All Locations
	gross: number;
	net: number;
}

export interface ILocationSales {
	locationBase: string;
	digitalParticipation: number;
	averageFeedback: number;
}


export interface ICampaign {
	locationBase: string;
	programId: string;
	title: string;
	subtitle: string;
	enabled: boolean;
	available: boolean;
	budgetId: string | null;
	created: number;
	modified: number | null;
}

export interface IConvertState {
	locations: ILocation[];
	campaigns: ICampaign[];
	budgets: IBudget[];
	sales: ISales[];
}

export interface ISalesSummaryInterval {
	gross: number;
	net: number;
	timestamp: number;
}

export interface ISalesSummary {
	day: {
		current: ISalesSummaryInterval;
		last: ISalesSummaryInterval;
	};
	week: {
		currentSales: ISalesSummaryInterval[];
		lastSales: ISalesSummaryInterval[];
		current: ISalesSummaryInterval;
		last: ISalesSummaryInterval;
	};
	month: {
		currentSales: ISalesSummaryInterval[];
		current: ISalesSummaryInterval;
		last: ISalesSummaryInterval;
	};
	year: {
		current: ISalesSummaryInterval;
	};
}

export interface ICustomerEngagement {
	locationBase: string | null;
	digitalParticipation: number;
	averageFeedback: number;
}

export type TInterval = 'hour' | 'day' | 'week' | 'month' | 'year';


@Module({dynamic: true, store, name: 'app'})
class Convert extends VuexModule implements IConvertState {

	public locations: ILocation[] = [];
	public campaigns: ICampaign[] = [];
	public budgets: IBudget[] = [];
	public sales: ISales[] = [];
	public billingSales: IBillingSale[] = [];
	public totalSales: ITotalSales[] = [];
	public salesSummary: ISalesSummary | null = null;
	public customerEngagements: ICustomerEngagement[] = [];

	get campaignsByLocationBase() {
		return (locationBase: string) => {
			return filter(this.campaigns, {locationBase: locationBase});
		};
	}

	get locationByBase() {
		return (locationBase: string): ILocation | null => {
			return find(this.locations, {base: locationBase}) as ILocation || null;
		};
	}

	get budgetsByLocationBaseAndProgramId() {
		return (locationBase: string, programId: string) => {
			return sortBy(filter(this.budgets, {
				locationBase: locationBase,
				programId: programId,
			}), 'order');
		};
	}

	get campaignByLocationBaseAndProgramId() {
		return (locationBase: string, programId: string) => {
			return find(this.campaigns, {
				locationBase: locationBase,
				programId: programId,
			}) || null;
		};
	}

	get budgetById() {
		return (id: string) => {
			return find(this.budgets, {id: id});
		};
	}

	get salesByLocationBaseAndInterval() {
		return (locationBase: string | null, interval: TInterval) => {
			return filter(this.sales, {
				locationBase: locationBase,
				interval: interval
			});
		};
	}

	get billingSalesByLocationBaseAndIntervalAndStartEndDates() {
		return (locationBase: string | null, interval: TInterval, start: number, end: number) => {
			return filter(this.billingSales, (sale) => {
				return sale.locationBase === locationBase &&
					sale.interval === interval &&
					sale.timestamp >= start &&
					sale.timestamp < end;
			});
		};
	}

	get customerEngagementsByLocationBase() {
		return (locationBase: string | null) => {
			return find(this.customerEngagements, {
				locationBase: locationBase
			}) || {
				locationBase: locationBase,
				digitalParticipation: 0,
				averageFeedback: 0
			};
		};
	}

	get totalSalesByLocationBase() {
		return (locationBase: string | null) => {
			return find(this.totalSales, {
				locationBase: locationBase
			}) || {
				locationBase: locationBase,
				gross: 0,
				net: 0
			};
		};
	}

	public static mergeAndAdd(existingItems: any[], newItems: any[], predicateKeys: string[] = ['base']) {
		newItems.forEach((newItem) => {
			const existingItem = find(existingItems, pick(newItem, predicateKeys));
			if(existingItem) {
				Object.assign(existingItem, newItem);
			} else {
				existingItems.push(newItem);
			}
		});
	}

	@Action
	public setLocations(locations: ILocation[]) {
		this.SET_LOCATIONS(locations);
	}

	@Action
	public addCampaigns(campaigns: ICampaign[]) {
		this.ADD_CAMPAIGNS(campaigns);
	}

	@Action
	public addBudgets(budgets: IBudget[]) {
		this.ADD_BUDGETS(budgets);
	}

	@Action
	public addSales(sales: ISales[]) {
		this.ADD_SALES(sales);
	}

	@Action
	public addBillingSales(billingSales: IBillingSale[]) {
		this.ADD_BILLING_SALES(billingSales);
	}

	@Action
	public setSalesSummary(salesSummary: ISalesSummary) {
		this.SET_SALES_SUMMARY(salesSummary);
	}

	@Action
	public addCustomerEngagement(customerEngagement: ICustomerEngagement) {
		this.ADD_CUSTOMER_ENGAGEMENT(customerEngagement);
	}

	@Action
	public addTotalSales(totalSales: ITotalSales) {
		this.ADD_TOTAL_SALES(totalSales);
	}

	@Action
	public setActiveBudgetBase(data: {locationBase: string, programId: string, budgetId: string}) {
		this.SET_ACTIVE_BUDGET_BASE(data);
	}

	@Mutation
	public SET_LOCATIONS(locations: ILocation[]) {
		this.locations = locations;
	}

	@Mutation
	public ADD_CAMPAIGNS(campaigns: ICampaign[]) {
		Convert.mergeAndAdd(this.campaigns, campaigns, ['locationBase', 'programId']);
	}

	@Mutation
	public ADD_BUDGETS(budgets: IBudget[]) {
		Convert.mergeAndAdd(this.budgets, budgets, ['id', 'locationBase', 'programId']);
	}

	@Mutation
	public ADD_SALES(sales: ISales[]) {
		Convert.mergeAndAdd(this.sales, sales, ['locationBase', 'interval', 'timestamp']);
	}

	@Mutation
	public ADD_BILLING_SALES(billingSales: IBillingSale[]) {
		Convert.mergeAndAdd(this.billingSales, billingSales, ['locationBase', 'interval', 'timestamp']);
	}

	@Mutation
	public SET_SALES_SUMMARY(salesSummary: ISalesSummary) {
		this.salesSummary = salesSummary;
	}

	@Mutation
	public ADD_CUSTOMER_ENGAGEMENT(customerEngagement: ICustomerEngagement) {
		Convert.mergeAndAdd(this.customerEngagements, [customerEngagement], ['locationBase']);
	}

	@Mutation
	public ADD_TOTAL_SALES(totalSales: ITotalSales) {
		Convert.mergeAndAdd(this.totalSales, [totalSales], ['locationBase']);
	}

	@Mutation
	public SET_ACTIVE_BUDGET_BASE(data: {locationBase: string, programId: string, budgetId: string}) {
		const campaign = find(this.campaigns, {
			locationBase: data.locationBase,
			programId: data.programId
		}) || null;
		if(campaign) {
			campaign.budgetId = data.budgetId;
		}
	}
}

export const ConvertModule = getModule(Convert);

